'use client';
import PropTypes from 'prop-types';
import {useState} from 'react';
import dynamic from 'next/dynamic';
import {useFormContext} from 'react-hook-form';
import {IconX} from '@tabler/icons-react';
import {Button, Divider, FormHelperText, Grid, Typography} from '@mui/material';
import {RHFMultilingualField} from '@/shared/components/forms/theme-elements/form/RHFMultilingualField';
const FileUploader = dynamic(
  () =>
    import('@/shared/components/forms/theme-elements/form/fileUploader').then(
      (mod) => mod.FileUploader
    ),
  {ssr: false}
);

export const List = ({name, values, errors}) => {
  const [fileLoading, setFileLoading] = useState(false);

  const {setValue} = useFormContext();

  const removeLocation = ({locationIndex}) => {
    setValue(
      name,
      (values || []).filter((_, ind) => ind !== locationIndex)
    );
  };

  const removeHighlight = ({locationIndex, highlightIndex}) => {
    setValue(
      `${name}[${locationIndex}].highlights`,
      (values?.[locationIndex]?.highlights || []).filter(
        (_, ind) => ind !== highlightIndex
      )
    );
  };

  const addLocation = () =>
    setValue(name, [
      ...(values || []),
      {
        locationName: {en: '', nl: ''},
        highlights: [],
        descriptionTitle: {en: '', nl: ''},
        description: {en: '', nl: ''},
        images: [],
      },
    ]);

  const addHighlight = ({locationIndex}) =>
    setValue(`${name}[${locationIndex}].highlights`, [
      ...(values?.[locationIndex]?.highlights || []),
      {
        en: '',
        nl: '',
      },
    ]);

  return (
    <Grid>
      {values?.map((location, locationIndex) => {
        return (
          <Grid key={location.id || `location-${locationIndex}`}>
            <Grid>
              <Grid
                display='flex'
                alignItems='center'
                justifyContent={'space-between'}
                mb={2}
              >
                <Typography variant='h6'>
                  {locationIndex + 1}. Location Name
                </Typography>
                <IconX
                  color='red'
                  onClick={() => removeLocation({locationIndex})}
                />
              </Grid>

              <RHFMultilingualField
                name={`${name}[${locationIndex}].locationName`}
              />
            </Grid>

            <Typography variant='h6' mb={2}>
              Highlights
            </Typography>
            {location?.highlights?.length > 0 && (
              <Grid>
                {location.highlights?.map((highlight, highlightIndex) => {
                  return (
                    <Grid container>
                      <Grid item md={10}>
                        <RHFMultilingualField
                          name={`${name}[${locationIndex}].highlights[${highlightIndex}]`}
                        />
                      </Grid>
                      <Grid item md={2}>
                        <Button
                          color='error'
                          fontWeight={600}
                          textAlign={'right'}
                          sx={{
                            float: 'right',
                            height: '67%',
                            cursor: 'pointer !important',
                          }}
                          onClick={() =>
                            removeHighlight({locationIndex, highlightIndex})
                          }
                        >
                          <IconX />
                        </Button>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            <center>
              <Button
                size='small'
                color={'error'}
                onClick={() => addHighlight({locationIndex})}
              >
                Add New Highlight
              </Button>
            </center>

            <Typography variant='h6' mb={2}>
              Description Title
            </Typography>
            <RHFMultilingualField
              name={`${name}[${locationIndex}].descriptionTitle`}
            />

            <Typography variant='h6' mb={2}>
              Description
            </Typography>
            <RHFMultilingualField
              name={`${name}[${locationIndex}].description`}
            />

            <Typography variant='h6' mb={2}>
              Image
            </Typography>
            <FileUploader
              meta={{shape: 'Rectangle', size: {width: 570, height: 370}}}
              label={'Location Images'}
              name={`${name}[${locationIndex}].images`}
              setFileUrl={(links) =>
                setValue(`${name}[${locationIndex}].images`, links)
              }
              destination={'landing-pages'}
              values={values?.[locationIndex]?.images}
              setFileLoading={setFileLoading}
              setValue={setValue}
            />

            <Divider sx={{my: 2}} />
          </Grid>
        );
      })}

      <center>
        <Button size='small' color={'error'} onClick={() => addLocation()}>
          Add New Location
        </Button>
      </center>

      {errors && (
        <FormHelperText sx={{color: 'error.main', ml: -0.01, mb: 1}}>
          {errors?.message}
        </FormHelperText>
      )}
    </Grid>
  );
};

List.propTypes = {
  name: PropTypes.string.isRequired,
  values: PropTypes.array,
  errors: PropTypes.object,
};
